import { Injectable, inject } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { BarcodeScanner } from '@capawesome-team/capacitor-barcode-scanner';
import { Platform } from '@ionic/angular';
import { ForegroundService } from '@capawesome-team/capacitor-android-foreground-service';
import { LocalNotifications } from '@capacitor/local-notifications';

@Injectable({ providedIn: 'root' })
export class PermissionsService {

  private platform = inject(Platform);
  private get isNative(): boolean { return Capacitor.isNativePlatform(); }
  private get isAndroid(): boolean { return Capacitor.getPlatform() === 'android'; }

  /**
   * checks the permissions the app needs to run
   * and returns whether all necessary permissions have been granted
   **/
  public async hasAllNecessaryPermissions(): Promise<PermissionSummary> {
    try {
      console.log(await BackgroundGeolocation.getProviderState())
      //  all settled so that we don't want all the calls to fail if 1 does
      const permissions = await Promise.allSettled([
        this.hasBackgroundLocationPermissions(),
        this.hasCameraPermissions(),
        this.hasLocationPermissions(),
        this.hasDisplayOverPermission(),
        this.hasNotificationPermission()
      ]).then(responses => responses.map(res => (res.status === 'fulfilled' ? res.value : null)));
      const [backgroundLocation, camera, location, displayOverOther, notifications] = permissions;
      return {
        //  iOS is not gated on background location here: LocationService.init() requests the
        //  "Always" upgrade itself and fails when the user refuses.
        hasAllNecessaryPermissions: this.isAndroid
          ? ![displayOverOther, backgroundLocation, location].some(value => !value)
          : ![displayOverOther, location].some(value => !value),
        permissions: { backgroundLocation, camera, displayOverOther, location, fitness: null, notifications },
      };
    } catch (err) {
      console.error(err);
      return {
        hasAllNecessaryPermissions: true,
        permissions: {
          backgroundLocation: null,
          camera: null,
          displayOverOther: null,
          location: null,
          fitness: null,
          notifications: null
        },
      };
    }
  }

  public hasPermission(permission: AppPermission): Promise<boolean> {
    switch (permission) {
      case 'backgroundLocation': return this.hasBackgroundLocationPermissions();
      case 'location': return this.hasLocationPermissions();
      case 'camera': return this.hasCameraPermissions();
      case 'displayOverOther': return this.hasDisplayOverPermission();
      case 'notifications': return this.hasNotificationPermission()
      default: throw new Error(`${permission} check not found`);
    }
  }

  private async hasLocationPermissions() {
    if (!this.isNative) {
      const position = await new Promise(resolve => {
        navigator.geolocation.getCurrentPosition(
          position => resolve(position),
          error => { console.error(error); resolve(null); },
        );
      });
      return !!position;
    }
    await this.platform.ready();
    const status = await Geolocation.checkPermissions();
    return status.location === 'granted';
  }

  private async hasBackgroundLocationPermissions(): Promise<boolean> {
    //  although not technically correct, no such thing in web really
    if (!this.isNative) { return true; }
    await this.platform.ready();
    //  getProviderState() only reads the current authorization; it does not need
    //  BackgroundGeolocation.ready() and never prompts. On Android, ALWAYS is reported
    //  exactly when ACCESS_BACKGROUND_LOCATION is granted; on iOS it is the "Always" authorization.
    const providerState = await BackgroundGeolocation.getProviderState()
      .catch(err => { console.log(err); return null; });
    return providerState?.status === BackgroundGeolocation.AUTHORIZATION_STATUS_ALWAYS;
  }

  private async hasCameraPermissions(): Promise<boolean> {
    if (!this.isNative) { return true; }
    await this.platform.ready();
    const status = await BarcodeScanner.checkPermissions();
    return status.camera === 'granted';
  }

  private async hasDisplayOverPermission(): Promise<boolean> {
    if (!this.isAndroid ) { return true; }
    const permission = await ForegroundService.checkManageOverlayPermission();
    return !!permission?.granted;
  }

  private async hasNotificationPermission(): Promise<boolean> {
    const permission = await LocalNotifications.checkPermissions()
    return permission.display === 'granted'
  }
}


export const appPermissions = [
  'backgroundLocation',
  'camera',
  'displayOverOther',
  'location',
  'notifications'
] as const;
export type AppPermissions = typeof appPermissions;
export type AppPermission = AppPermissions[number];
export type PermissionSummary = {
  hasAllNecessaryPermissions: boolean;
  permissions: {
    backgroundLocation: boolean;
    camera: boolean;
    displayOverOther: boolean;
    location: boolean;
    fitness: boolean;
    notifications: boolean;
  };
};
